import React, { useCallback, useContext, useState } from "react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { GetCustomerUsers_customer_users as umsUser } from "src/api/generated/GetCustomerUsers";
import styles from "./CustomerUserResetButton.module.css";
import { getRoleKey } from "src/network/User";
import { NetworkConfig } from "src/network/NetworkConfig";
import Axios from "src/network/Axios";

interface UserResetButtonProps {
    user: umsUser;
    userid: string;
}

const CustomerUserResetButtonBase = (props: UserResetButtonProps) => {
    const { t } = useTranslation();
    const { uiStore, authStore } = useContext(RootStoreContext);
    const [isUserResetModalDisplaying, setIsUserResetModalDisplaying] = useState(false);
    const [userIsReseting, setUserIsReseting] = useState(false);

    const resetUserStatus = useCallback(async () => {
        const user = props.user;

        if (authStore.user && user) {
            try {
                await Axios.getAuthInstance().post(
                    NetworkConfig.updateUserStatusUrl,
                    {
                        action: "RESET",
                        registrationcode: user.registrationcode,
                        email: user.email ?? "",
                        userid: user.userid,
                        customerid: user.customerid
                    },
                    {
                        headers: {
                            "x-hasura-role": getRoleKey(authStore.user.role)
                        }
                    }
                );
                uiStore.printStatusMessage(t("screens.customer_users.user_is_reset"), MessageType.INFO);
            } catch (error: any) {
                const errorMessage = `${t("screens.customer_users.error_reset_user")} {${error.response?.data?.message || error}}`;
                uiStore.printStatusMessage(errorMessage, MessageType.ERROR);
                console.error(errorMessage);
            }
        }
    }, [authStore.user, props.user, uiStore, t]);

    const resetUser = useCallback(async () => {
        if (props.user) {
            setUserIsReseting(true);

            await resetUserStatus();

            uiStore.setIsCustomerUserReset(true);

            setUserIsReseting(true);
            setIsUserResetModalDisplaying(false);
            setUserIsReseting(false);
        }
    }, [props.user, resetUserStatus, uiStore]);

    const toggleResetUserModal = useCallback(() => {
        setIsUserResetModalDisplaying((isDisplaying) => !isDisplaying);
    }, []);

    const isUserRegisterd = !props.user.registered || false;

    return (
        <>
            <UI.Button
                label={t("screens.customer_users.reset")}
                className={styles.ResetButton}
                onClick={toggleResetUserModal}
                disabled={isUserRegisterd}
                variant={isUserRegisterd ? "secondary" : "danger"}
            />

            <UI.Modal
                backdrop={"static"}
                title={t("screens.customer_users.reset_user").toString()}
                show={isUserResetModalDisplaying}
                onClose={toggleResetUserModal}
            >
                <div>
                    <p>{t("screens.customer_users.question_reset_user")}</p>
                    <p>{t("screens.customer_users.reset_reference")}</p>

                    {userIsReseting ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span>{t("screens.customer_users.user_is_going_to_be_reset")}</span>
                            <UI.RotatingSpinner noLogo={true} size={35} className={"align-items-start"} />
                        </div>
                    ) : (
                        <UI.Button
                            label={t("labels.yes")}
                            disabled={userIsReseting}
                            onClick={resetUser}
                            className={styles.YesButton}
                            variant="danger"
                        />
                    )}
                </div>
            </UI.Modal>
        </>
    );
};

export const CustomerUserResetButton = observer(CustomerUserResetButtonBase);
